import { memo, useMemo, useState } from 'react';
import { Box, Flex, Table, Tbody, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuthContext } from 'context/authContext';
import NoTableDataIcon from 'components/Icons/NoTableDataIcon';
import { STATUS } from 'projectConstants';
import { ExportDataRequestBody } from 'models/requestBody/createExportData.interface';
import {
  getBlockDetails,
  getDimension,
  getExpandKey,
  getIndicatorTableStatus,
  getModelDetails,
  getTableData,
  getTableFilter,
  getTableHeaderData,
  overallIndicatorFilter,
  setExpandKey,
  setTableFilter,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import apiService from 'services';
import saveFile, { buildBlockExportFileName } from 'utils/file';
import { AppDispatch } from 'utils/GlobalHelpers';

import {
  PLAN_CARD_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TABLE_CELL_PX,
  PLAN_TABLE_CELL_PY,
  PLAN_TABLE_HEADER_BG,
  PLAN_TABLE_STICKY_BG,
  PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
  PLAN_TEXT,
  accent,
  text as textToken,
} from '../planPageRevampUi';

import PlanCard from './PlanCard';
import PlanCardToolbarStrip from './PlanCardToolbarStrip';
import PlanDataTableRow from './PlanDataTableRow';
import PlanDataTableBodySkeleton from './PlanDataTableBodySkeleton';

import type { PlanTimeFilterValue } from './PlanTimeFilterSegment';

interface Properties {
  onToggleAuthModal: () => void;
  pageScrollMode?: boolean;
}

const PlanDataTable = ({ onToggleAuthModal, pageScrollMode = false }: Properties) => {
  const { blockId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const dispatch: AppDispatch = useDispatch();
  const tableData = useSelector(getTableData);
  const tableHeaderData = useSelector(getTableHeaderData);
  const tableFilter = useSelector(getTableFilter);
  const expandKey = useSelector(getExpandKey);
  const modelDetail = useSelector(getModelDetails);
  const blockDetails = useSelector(getBlockDetails);
  const dimension = useSelector(getDimension);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const baseScenario = useSelector(getBaseScenario);
  const tableStatus = useSelector(getIndicatorTableStatus);
  const [isExporting, setIsExporting] = useState(false);

  const timeAggregation = modelDetail?.time_properties?.time_granularity
    ? String(modelDetail.time_properties.time_granularity).toUpperCase()
    : 'M';

  const visibleHeaders = (tableHeaderData || []).filter((item: any) => item?.isShow);

  const onExpandHeader = (header: any) => {
    if (!header?.isShowExpandable || timeAggregation === 'Y') return;
    if (expandKey !== header.name && (tableFilter === 'Y' || tableFilter === 'Q')) {
      dispatch(setExpandKey(header.name));
    } else {
      dispatch(setExpandKey(''));
    }
  };

  const exportRequestBody: ExportDataRequestBody = useMemo(
    () => ({
      orient: 'table',
      query_params: {
        rows: [{ name: 'Indicators', filter: indicatorFilter }, { name: dimension?.name || null }],
        columns: [{ name: 'Time', display_levels: [tableFilter] }],
        filters: [],
      },
      scenario_id: baseScenario?.id,
    }),
    [baseScenario?.id, dimension?.name, indicatorFilter, tableFilter],
  );

  const { refetch: fetchExportData } = useQuery(
    ['export', blockId, baseScenario?.id, tableFilter],
    () =>
      apiService.create_block_export({
        block_id: Number(blockId),
        request_body: exportRequestBody,
      }),
    {
      enabled: false,
      onError: (response: any) => {
        if (response.response?.data?.message) {
          navigate('/error');
        }
      },
    },
  );

  const onDownload = async () => {
    if (user?.is_anonymous) {
      onToggleAuthModal();
      return;
    }

    setIsExporting(true);
    try {
      const { data: exportData } = await fetchExportData();
      if (exportData) {
        const fileName = buildBlockExportFileName(blockDetails?.name);
        const blob = [exportData.data];
        const contentType =
          exportData.headers?.['content-type'] ??
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        saveFile(fileName, blob, contentType);
      }
    } finally {
      setIsExporting(false);
    }
  };

  const onTimeFilterChange = (filterKey: PlanTimeFilterValue) => {
    dispatch(setTableFilter(filterKey));
  };

  const isTableLoading =
    tableStatus === STATUS.LOADING || (tableStatus === STATUS.IDLE && !tableData?.length);
  const hasCachedTable = Boolean((tableData as unknown[])?.length);
  const showTableSkeleton = isTableLoading && !hasCachedTable;
  const isTableEmpty =
    tableStatus === STATUS.SUCCESS && (!tableData || (tableData as unknown[]).length === 0);

  const visibleRows = (tableData as any[]) || [];

  const metricColumnStyles = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 3,
    bg: PLAN_TABLE_STICKY_BG,
    py: PLAN_TABLE_CELL_PY,
    px: PLAN_TABLE_CELL_PX,
    minW: PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_TABLE_CELL_BORDER,
    ...PLAN_TEXT.tableComfortaa,
    color: textToken.primary,
    textTransform: 'none' as const,
    letterSpacing: 'normal',
  };

  const periodHeaderStyles = {
    py: PLAN_TABLE_CELL_PY,
    px: PLAN_TABLE_CELL_PX,
    bg: PLAN_TABLE_HEADER_BG,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_TABLE_CELL_BORDER,
    ...PLAN_TEXT.tableComfortaa,
    color: textToken.primary,
    textTransform: 'none' as const,
    letterSpacing: 'normal',
    textAlign: 'end' as const,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <PlanCard flex={pageScrollMode ? '1 1 auto' : '1 1 0'} minH={pageScrollMode ? undefined : 0}>
      <PlanCardToolbarStrip
        hint='Expand the table to drill into the values for each of your items'
        showTimeToggle={timeAggregation !== 'Y'}
        timeValue={tableFilter as PlanTimeFilterValue}
        onTimeChange={onTimeFilterChange}
        onDownload={onDownload}
        isExporting={isExporting}
      />

      {/* Figma 76685:5657 — the grid is inset from the card by 16px and sits
          in a bordered, 8px-radius box of its own rather than running to the
          card's edges. Two nested boxes: this one owns the padding, the inner
          one owns the border and clips the scroll to its rounded corners. */}
      <Box
        flex={pageScrollMode ? undefined : '1'}
        minH={pageScrollMode ? undefined : 0}
        minW={0}
        p={PLAN_CARD_PADDING}
        display='flex'
        flexDirection='column'
      >
        <Box
          flex={pageScrollMode ? undefined : '1'}
          minH={pageScrollMode ? undefined : 0}
          minW={0}
          border='1px solid'
          borderColor={PLAN_TABLE_CELL_BORDER}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          overflowX='auto'
          overflowY={pageScrollMode ? 'visible' : 'auto'}
          className={pageScrollMode ? undefined : 'scroll'}
          position='relative'
        >
          {showTableSkeleton && <PlanDataTableBodySkeleton />}
          {!showTableSkeleton && isTableEmpty && (
            <Flex h='200px' direction='column' gap={2} alignItems='center' justifyContent='center'>
              <NoTableDataIcon width={28} height={28} color={PLAN_TABLE_CELL_BORDER} />
              <Text {...PLAN_TEXT.body} color={textToken.secondary} textAlign='center'>
                No table data available for the current filters.
              </Text>
            </Flex>
          )}
          {!showTableSkeleton && !isTableEmpty && (
            <Table size='sm' variant='unstyled'>
              <Thead position='sticky' top={0} zIndex={2}>
                <Tr>
                  <Th {...metricColumnStyles}></Th>
                  {visibleHeaders.map((item: any, index: number) => {
                    const isExpanded = expandKey === item?.name;
                    const isExpandable = Boolean(item?.isShowExpandable) && timeAggregation !== 'Y';

                    return (
                      <Th
                        key={`${item?.name ?? ''}-${index}`}
                        {...periodHeaderStyles}
                        cursor={isExpandable ? 'pointer' : 'default'}
                        onClick={() => onExpandHeader(item)}
                      >
                        <Flex alignItems='center' justifyContent='flex-end' gap={1}>
                          {isExpandable &&
                            (isExpanded ? (
                              <ChevronRightIcon boxSize={3} color={accent.solid} />
                            ) : (
                              <ChevronLeftIcon boxSize={3} color={accent.solid} />
                            ))}
                          {item?.name}
                        </Flex>
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {visibleRows.map((row: any, index: number) => (
                  <PlanDataTableRow
                    key={(row?.name ?? '') + index}
                    row={row}
                    expandKey={expandKey}
                  />
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </Box>
    </PlanCard>
  );
};

export default memo(PlanDataTable);
